<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1 dzen-sources-page">
    <!-- Compact header with bulk actions (matches Telegram style) -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto" class="d-flex align-center ga-2">
        <v-select
          v-model="selectedBulkAction"
          :items="bulkActions"
          item-title="text"
          item-value="value"
          placeholder="Выберите действие"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          :disabled="selectedItems.length === 0"
          @update:model-value="executeBulkAction"
          style="min-width: 200px"
        ></v-select>
      </v-col>
      <v-col class="text-center">
        <h4 class="mb-0">Источники Дзен</h4>
      </v-col>
      <v-col cols="auto">
        <div class="d-flex align-center ga-2">
          <span class="text-caption">На странице:</span>
          <v-select
            v-model="limit"
            :items="[5, 10, 20, 50, 100, 250, 500]"
            variant="outlined"
            density="compact"
            hide-details
            style="max-width: 160px"
            @update:model-value="onPageChange(1)"
          ></v-select>
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            size="small"
            @update:model-value="onPageChange"
          ></v-pagination>
          <span class="text-caption font-weight-bold"> Найдено: {{ totalItems }}</span>
          <v-btn
            :icon="panelVisible ? 'mdi-filter-off' : 'mdi-filter'"
            variant="outlined"
            @click="toggleDzenPanel"
            :color="panelVisible ? 'primary' : 'default'"
            class="ml-2"
          ></v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <!-- Main table column -->
      <v-col :cols="panelVisible ? 10 : 12" class="table-column-transition">
        <v-card>
          <v-card-text class="pa-0">
            <!-- server table: pages and sorting come from the API (a client v-data-table sorted
                 only the loaded page) -->
            <v-data-table-server
              :headers="headers"
              :items="sources"
              :items-length="totalItems"
              :items-per-page="limit"
              :loading="loading"
              :sort-by="sortBy"
              @update:sort-by="updateSort"
              class="elevation-1"
              hover
              fixed-header
              height="calc(100vh - 236px)"
            >
              <!-- Checkbox column -->
              <template v-slot:[`item.checkbox`]="{ item }">
                <v-checkbox
                  v-model="selectedItems"
                  :value="item.id"
                  hide-details
                  density="compact"
                ></v-checkbox>
              </template>

              <!-- Logo column -->
              <template v-slot:[`item.logo`]="{ item }">
                <v-avatar v-if="item.ext_thumbnail_url" size="40">
                  <v-img :src="item.ext_thumbnail_url" cover />
                </v-avatar>
                <v-avatar v-else size="40">
                  <v-icon color="grey-lighten-1">mdi-fire</v-icon>
                </v-avatar>
              </template>

              <!-- Channel name as link -->
              <template v-slot:[`item.display_name`]="{ item }">
                <a
                  v-if="item.dzen_channel_url"
                  :href="item.dzen_channel_url"
                  target="_blank"
                  class="text-primary text-decoration-none"
                  @click.stop
                >
                  {{ item.display_name || '—' }}
                </a>
                <span v-else>{{ item.display_name || '—' }}</span>
              </template>

              <!-- Status in Russian -->
              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  :color="statusColor(item.status)"
                  size="x-small"
                  label
                >
                  {{ statusLabel(item.status) }}
                </v-chip>
              </template>

              <!-- Section (Topic) -->
              <!-- one topic in the cell, all of them in the tooltip -->
              <template v-slot:[`item.section`]="{ item }">
                <v-tooltip v-if="topicNames(item.topic_ids).length > 1" :text="topicNames(item.topic_ids).join(', ')" location="top">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">{{ topicNames(item.topic_ids)[0] }}…</span>
                  </template>
                </v-tooltip>
                <span v-else>{{ topicNames(item.topic_ids)[0] || '—' }}</span>
              </template>

              <!-- Geolocation -->
              <template v-slot:[`item.geo`]="{ item }">
                {{ item.geolocation_name || '—' }}
              </template>

              <!-- Subscribers count -->
              <template v-slot:[`item.subscribers_count`]="{ item }">
                <div class="text-right">{{ formatNumber(item.subscribers_count) }}</div>
              </template>

              <!-- Updated -->
              <template v-slot:[`item.updated_at`]="{ item }">
                {{ formatISODate(item.updated_at) }}
              </template>

              <!-- Messages count (clickable) -->
              <template v-slot:[`item.messages_count`]="{ item }">
                <div
                  class="text-right"
                  :class="{ 'text-primary cursor-pointer': item.messages_count > 0 }"
                  @click="item.messages_count > 0 && openMessagesModal(item)"
                >
                  {{ formatNumber(item.messages_count) }}
                </div>
              </template>

              <!-- Monitoring (Да/Нет) -->
              <template v-slot:[`item.is_monitoring`]="{ item }">
                {{ item.is_monitoring ? 'Да' : 'Нет' }}
              </template>

              <!-- Description with truncation -->
              <template v-slot:[`item.description`]="{ item }">
                <v-tooltip v-if="item.description" :text="item.description" location="top">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">{{ truncateDescription(item.description) }}</span>
                  </template>
                </v-tooltip>
                <span v-else>—</span>
              </template>

              <!-- Actions column -->
              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                  <v-tooltip :text="item.is_monitoring ? 'Остановить' : 'Запустить'">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        :icon="item.is_monitoring ? 'mdi-stop' : 'mdi-play'"
                        size="x-small"
                        variant="text"
                        :color="item.is_monitoring ? 'warning' : 'success'"
                        @click="toggleMonitoring(item)"
                      ></v-btn>
                    </template>
                  </v-tooltip>
                  <v-tooltip text="Обновить информацию">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-information"
                        size="x-small"
                        variant="text"
                        :loading="item.is_processing"
                        @click="updateSourceInfo(item)"
                      ></v-btn>
                    </template>
                  </v-tooltip>
                  <v-tooltip text="Редактировать">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-pencil"
                        size="x-small"
                        variant="text"
                        color="primary"
                        @click="openEditModal(item)"
                      ></v-btn>
                    </template>
                  </v-tooltip>
                  <v-tooltip text="Удалить">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-delete"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click="confirmDelete(item)"
                        :disabled="item.is_monitoring"
                      ></v-btn>
                    </template>
                  </v-tooltip>
                </div>
              </template>

              <!-- Table header checkbox -->
              <template v-slot:[`header.checkbox`]>
                <v-checkbox
                  v-model="selectAll"
                  @update:model-value="toggleSelectAll"
                  hide-details
                  density="compact"
                ></v-checkbox>
              </template>

              <!-- No data -->
              <template v-slot:no-data>
                <v-alert type="info" variant="text">
                  Нет данных для отображения
                </v-alert>
              </template>

              <!-- Bottom slot -->
              <template v-slot:bottom>
                <div style="height: 1px;"></div>
              </template>
            </v-data-table-server>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Filters panel -->
      <v-slide-x-reverse-transition>
        <v-col v-if="panelVisible" cols="2" key="filters">
          <v-card>
            <v-card-title class="text-subtitle-1">Фильтры</v-card-title>
            <v-card-text class="pa-3">
              <!-- Subscribers range -->
              <div class="mb-4">
                <div class="text-subtitle-2 mb-2">Подписчики</div>
                <v-row dense>
                  <v-col cols="6">
                    <v-text-field
                      :model-value="dzenFilters.subscribers_from"
                      @update:model-value="updateDzenFilter({ field: 'subscribers_from', value: $event })"
                      type="number"
                      step="1"
                      min="0"
                      placeholder="от"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @blur="onFilterChange"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      :model-value="dzenFilters.subscribers_to"
                      @update:model-value="updateDzenFilter({ field: 'subscribers_to', value: $event })"
                      type="number"
                      step="1"
                      min="0"
                      placeholder="до"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @blur="onFilterChange"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- Updated date range -->
              <div class="mb-4">
                <div class="text-subtitle-2 mb-2">Дата обновления</div>
                <v-text-field
                  :model-value="dzenFilters.updated_from"
                  @update:model-value="updateDzenFilter({ field: 'updated_from', value: $event })"
                  type="date"
                  label="С"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="mb-2"
                  @change="onFilterChange"
                ></v-text-field>
                <v-text-field
                  :model-value="dzenFilters.updated_to"
                  @update:model-value="updateDzenFilter({ field: 'updated_to', value: $event })"
                  type="date"
                  label="По"
                  variant="outlined"
                  density="compact"
                  hide-details
                  @change="onFilterChange"
                ></v-text-field>
              </div>

              <!-- Title (display_name) -->
              <v-text-field
                :model-value="dzenFilters.search"
                @update:model-value="updateDzenFilter({ field: 'search', value: $event })"
                label="Заголовок"
                variant="outlined"
                density="compact"
                hide-details
                class="mb-4"
                @blur="onFilterChange"
              ></v-text-field>

              <!-- Description -->
              <v-text-field
                :model-value="dzenFilters.description"
                @update:model-value="updateDzenFilter({ field: 'description', value: $event })"
                label="Описание"
                variant="outlined"
                density="compact"
                hide-details
                class="mb-4"
                @blur="onFilterChange"
              ></v-text-field>

              <!-- Section (Topic) -->
              <v-select
                :model-value="dzenFilters.topic_id"
                @update:model-value="val => { updateDzenFilter({ field: 'topic_id', value: val }); onFilterChange() }"
                :items="sections"
                item-title="option"
                item-value="value"
                label="Раздел"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
              ></v-select>

              <!-- Geolocation -->
              <v-autocomplete
                :model-value="dzenFilters.geolocation_id"
                @update:model-value="val => { updateDzenFilter({ field: 'geolocation_id', value: val }); onFilterChange() }"
                :items="filterGeoSuggestions"
                item-title="label"
                item-value="value"
                label="Гео"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
                @update:search="searchFilterGeo"
                no-filter
                :no-data-text="'Введите название для поиска'"
              ></v-autocomplete>

              <!-- Messages range -->
              <div class="mb-4">
                <div class="text-subtitle-2 mb-2">Статей</div>
                <v-row dense>
                  <v-col cols="6">
                    <v-text-field
                      :model-value="dzenFilters.messages_from"
                      @update:model-value="updateDzenFilter({ field: 'messages_from', value: $event })"
                      type="number"
                      step="1"
                      min="0"
                      placeholder="от"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @blur="onFilterChange"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      :model-value="dzenFilters.messages_to"
                      @update:model-value="updateDzenFilter({ field: 'messages_to', value: $event })"
                      type="number"
                      step="1"
                      min="0"
                      placeholder="до"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @blur="onFilterChange"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- Monitoring status -->
              <v-select
                :model-value="dzenFilters.is_monitoring"
                @update:model-value="val => { updateDzenFilter({ field: 'is_monitoring', value: val }); onFilterChange() }"
                :items="monitoringOptions"
                item-title="text"
                item-value="value"
                label="Мониторинг"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
              ></v-select>

              <!-- Status -->
              <v-select
                :model-value="dzenFilters.status"
                @update:model-value="val => { updateDzenFilter({ field: 'status', value: val }); onFilterChange() }"
                :items="statusOptions"
                item-title="text"
                item-value="value"
                label="Статус"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
              ></v-select>

              <!-- Tags -->
              <v-autocomplete
                :model-value="dzenFilters.tags"
                @update:model-value="onTagsUpdate"
                :items="availableTags"
                item-title="name"
                item-value="id"
                label="Теги"
                multiple
                chips
                closable-chips
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
                @update:search="searchAvailableTags"
                return-object
                ref="tagFilterAutocomplete"
              >
                <template #chip="{ props, item }">
                  <v-chip
                    v-bind="props"
                    :text="item.raw.name"
                    closable
                    size="small"
                  ></v-chip>
                </template>
              </v-autocomplete>

              <!-- Filter actions -->
              <div class="d-flex justify-space-between">
                <v-btn
                  color="primary"
                  variant="outlined"
                  @click="resetFilters"
                  prepend-icon="mdi-filter-remove"
                >
                  Сбросить
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-slide-x-reverse-transition>
    </v-row>

    <!-- Messages Modal (Telegram-style layout) -->
    <v-dialog v-model="messagesModal.visible" max-width="1200px" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <div>
            {{ messagesModal.source?.display_name }}
            <small class="text-medium-emphasis ml-2">({{ messagesModal.totalMessages }} статей)</small>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" @click="messagesModal.visible = false"></v-btn>
        </v-card-title>

        <v-card-text class="pa-0" style="height: 70vh;">
          <div class="messages-container" ref="messagesContainer" @scroll="onMessagesScroll">
            <div v-if="messagesModal.loading && messagesModal.items.length === 0" class="text-center pa-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="mt-2">Загрузка...</div>
            </div>

            <div v-if="messagesModal.items.length === 0 && !messagesModal.loading" class="text-center pa-4 text-medium-emphasis">
              Статьи не найдены
            </div>

            <div v-for="(msg, idx) in messagesModal.items" :key="idx" class="message-item">
              <v-row>
                <!-- Left: date + link -->
                <v-col cols="12" md="3">
                  <div class="message-date">{{ formatDateOnly(msg.published_at) }}</div>
                  <div class="message-link" v-if="msg.source_url">
                    <a :href="msg.source_url" target="_blank" class="text-primary">
                      Оригинал
                      <v-icon size="small" class="ml-1">mdi-open-in-new</v-icon>
                    </a>
                  </div>
                </v-col>

                <!-- Right: article content -->
                <v-col cols="12" md="9">
                  <div class="message-content">
                    <!-- Publisher header: avatar + name + @handle (Dzen style) -->
                    <div v-if="dzenPublisher(msg).url" class="author-header mb-2">
                      <v-avatar size="32" class="mr-2">
                        <v-icon color="grey-lighten-1">mdi-newspaper-variant-outline</v-icon>
                      </v-avatar>
                      <div class="d-flex flex-column">
                        <span class="author-name">{{ dzenPublisher(msg).label }}</span>
                        <a
                          :href="dzenPublisher(msg).url"
                          target="_blank"
                          rel="noopener"
                          class="author-handle"
                        >{{ dzenPublisher(msg).handle ? '@' + dzenPublisher(msg).handle : dzenPublisher(msg).url }}</a>
                      </div>
                    </div>
                    <div v-if="msg.title" class="article-title">{{ msg.title }}</div>
                    <div class="main-text" style="white-space: pre-wrap; word-wrap: break-word;">
                      <template v-if="msg._expanded">{{ msg.content }}</template>
                      <template v-else>{{ truncateText(msg.content, 300) }}</template>
                    </div>
                    <div v-if="msg.content && msg.content.length > 300" class="mt-1">
                      <a
                        href="#"
                        class="text-primary text-decoration-none"
                        @click.prevent="msg._expanded = !msg._expanded"
                      >
                        {{ msg._expanded ? 'Скрыть' : 'Показать' }}
                      </a>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Loading more indicator -->
            <div v-if="messagesModal.loading && messagesModal.items.length > 0" class="text-center pa-3">
              <v-progress-circular indeterminate color="primary" size="small"></v-progress-circular>
            </div>

            <!-- All loaded -->
            <div v-if="!messagesModal.hasMore && messagesModal.items.length > 0" class="text-center pa-3 text-medium-emphasis">
              <small>Все статьи загружены</small>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Edit Modal -->
    <v-dialog v-model="editModal.visible" max-width="500">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-pencil</v-icon>
          Редактировать: {{ editModal.source?.display_name }}
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" @click="editModal.visible = false"></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-select
            v-model="editModal.form.section"
            :items="sections"
            item-title="option"
            item-value="value"
            label="Раздел"
            variant="outlined"
            density="compact"
            clearable
            class="mb-3"
          ></v-select>
          <v-autocomplete
            v-model="editModal.form.country"
            :items="geoSuggestions"
            item-title="label"
            item-value="value"
            label="Гео"
            variant="outlined"
            density="compact"
            clearable
            class="mb-3"
            @update:search="searchGeo"
            no-filter
            :no-data-text="'Введите название для поиска'"
          ></v-autocomplete>
          <v-select
            v-model="editModal.form.status"
            :items="statusOptions"
            item-title="text"
            item-value="value"
            label="Статус"
            variant="outlined"
            density="compact"
            clearable
            class="mb-3"
          ></v-select>
          <v-autocomplete
            v-model="editModal.form.tags"
            :items="editTagSuggestions"
            item-title="name"
            item-value="id"
            label="Теги"
            multiple
            chips
            closable-chips
            variant="outlined"
            density="compact"
            @update:search="searchEditTags"
            @keydown.enter="handleEditEnterKey"
            @update:model-value="onEditTagSelected"
            return-object
            clearable
            ref="editTagAutocomplete"
            hint="Enter — создать новый тег"
            persistent-hint
          >
            <template #chip="{ props, item }">
              <v-chip
                v-bind="props"
                :text="item.raw.name"
                closable
                size="small"
                :color="item.raw.id ? 'primary' : 'success'"
              />
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="editModal.visible = false">Отмена</v-btn>
          <v-btn color="primary" variant="flat" @click="saveEdit" :loading="editModal.saving">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Edit Modal -->
    <v-dialog v-model="bulkEditModal.visible" max-width="500" persistent @keydown.esc="closeBulkEditModal">
      <v-card>
        <v-card-title>
          <span class="text-h5">Массовое редактирование</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <div class="text-body-2 mb-2">
                  Выбрано источников: <strong>{{ selectedItems.length }}</strong>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="bulkEditModal.form.country"
                  :items="bulkGeoSuggestions"
                  item-title="label"
                  item-value="value"
                  label="Гео"
                  variant="outlined"
                  density="compact"
                  clearable
                  hint="Будет применено ко всем выбранным"
                  persistent-hint
                  @update:search="searchBulkGeo"
                  no-filter
                  :no-data-text="'Введите название для поиска'"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="bulkEditModal.form.section"
                  :items="sections"
                  item-title="option"
                  item-value="value"
                  label="Раздел"
                  variant="outlined"
                  density="compact"
                  clearable
                  hint="Будет применено ко всем выбранным"
                  persistent-hint
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="bulkEditModal.form.status"
                  :items="statusOptions"
                  item-title="text"
                  item-value="value"
                  label="Статус"
                  variant="outlined"
                  density="compact"
                  clearable
                  hint="Будет применено ко всем выбранным"
                  persistent-hint
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="bulkEditModal.form.tags"
                  :items="bulkEditTagSuggestions"
                  item-title="name"
                  item-value="id"
                  label="Добавить теги"
                  multiple
                  chips
                  closable-chips
                  variant="outlined"
                  density="compact"
                  @update:search="searchBulkEditTags"
                  @keydown.enter="handleBulkEditEnterKey"
                  @update:model-value="onBulkEditTagSelected"
                  return-object
                  clearable
                  ref="bulkEditTagAutocomplete"
                  hint="Теги будут добавлены к существующим"
                  persistent-hint
                >
                  <template #chip="{ props, item }">
                    <v-chip
                      v-bind="props"
                      :text="item.raw.name"
                      closable
                      size="small"
                      :color="item.raw.id ? 'primary' : 'success'"
                    />
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeBulkEditModal">Отменить</v-btn>
          <v-btn color="primary" variant="elevated" @click="saveBulkEdit" :disabled="!hasBulkEditChanges()">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog.visible" max-width="400">
      <v-card>
        <v-card-title>Удаление источника</v-card-title>
        <v-card-text>
          Вы уверены, что хотите удалить источник «{{ deleteDialog.source?.display_name }}»?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog.visible = false">Отмена</v-btn>
          <v-btn color="error" variant="flat" @click="deleteSource" :loading="deleteDialog.deleting">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient'
import apiDataClient from '@/apiDataClient'
import debounce from 'lodash.debounce'
import { mapGetters, mapActions, mapState } from 'vuex'

export default {
  name: 'DzenSources',

  data() {
    return {
      loading: false,
      sources: [],
      selectedItems: [],
      selectAll: false,
      selectedBulkAction: '',

      availableTags: [],
      editTagSuggestions: [],
      editCurrentSearchValue: '',
      bulkEditTagSuggestions: [],
      bulkEditCurrentSearchValue: '',
      sections: [],
      countries: [],
      geoSuggestions: [],
      bulkGeoSuggestions: [],
      filterGeoSuggestions: [],

      headers: [
        { title: '', key: 'checkbox', sortable: false, width: 50 },
        { title: '', key: 'logo', sortable: false, width: 50 },
        { title: 'Название', key: 'display_name', width: 220 },
        { title: 'Статус', key: 'status', width: 100 },
        { title: 'Раздел', key: 'section', width: 100, sortable: false },
        { title: 'Гео', key: 'geo', width: 120, sortable: false },
        { title: 'Подписчики', key: 'subscribers_count', align: 'end', width: 110 },
        { title: 'Обновлено', key: 'updated_at', width: 130 },
        { title: 'Статей', key: 'messages_count', align: 'end', width: 100 },
        { title: 'Следить', key: 'is_monitoring', width: 70 },
        { title: 'Описание', key: 'description', width: 180 },
        { title: 'Действия', key: 'actions', sortable: false, width: 140 }
      ],

      bulkActions: [
        { text: 'Редактировать', value: 'edit' },
        { text: 'Обновить информацию', value: 'update-info' },
        { text: 'Запустить мониторинг', value: 'start' },
        { text: 'Остановить мониторинг', value: 'stop' }
      ],

      monitoringOptions: [
        { text: 'Включен', value: true },
        { text: 'Выключен', value: false }
      ],

      statusOptions: [
        { text: 'Активен', value: 'active' },
        { text: 'В ожидании', value: 'pending' },
        { text: 'Заблокирован', value: 'blocked' },
        { text: 'Удалён', value: 'deleted' }
      ],

      messagesModal: {
        visible: false,
        source: null,
        items: [],
        loading: false,
        offset: 0,
        hasMore: true,
        totalMessages: 0
      },

      editModal: {
        visible: false,
        source: null,
        saving: false,
        form: {
          section: null,
          country: null,
          status: null,
          tags: []
        }
      },

      bulkEditModal: {
        visible: false,
        form: {
          section: null,
          country: null,
          status: null,
          tags: []
        }
      },

      deleteDialog: {
        visible: false,
        source: null,
        deleting: false
      },

      snackbar: {
        visible: false,
        text: '',
        color: 'success'
      },

      processingPollInterval: null,
    }
  },

  computed: {
    ...mapGetters('filters', ['dzenFilters']),
    ...mapState('filters', ['panelVisible']),
    currentPage: {
      get() { return this.dzenFilters.currentPage },
      set(val) { this.setDzenPagination({ currentPage: val }) }
    },
    limit: {
      get() { return this.dzenFilters.limit },
      set(val) { this.updateDzenFilter({ field: 'limit', value: val }) }
    },
    totalItems() {
      return this.dzenFilters.totalItems
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.limit) || 1
    },
    sortBy() {
      if (this.dzenFilters.sort_by) {
        return [{ key: this.dzenFilters.sort_by, order: this.dzenFilters.sort_order || 'desc' }]
      }
      return []
    },
    processingSourceIds() {
      return this.sources.filter(s => s.is_processing).map(s => s.id)
    }
  },

  mounted() {
    this.setActiveFilterSet('dzen')
    this.loadDzenFiltersFromCache()
    this.fetchData()
    this.loadReferenceData()
  },

  beforeUnmount() {
    this.stopProcessingPoll()
  },

  watch: {
    // the header checkbox follows the selection: a bulk command, an edit or a new page clears it
    selectedItems(val) {
      if (!val.length) this.selectAll = false
    },
  },

  methods: {
    ...mapActions('filters', [
      'setActiveFilterSet',
      'updateDzenFilter',
      'updateDzenFilters',
      'resetDzenFilters',
      'setDzenPagination',
      'setDzenSorting',
      'loadDzenFiltersFromCache',
      'saveDzenFiltersToCache',
      'toggleDzenPanel'
    ]),

    onFilterChange: debounce(function () {
      this.setDzenPagination({ currentPage: 1 })
      this.fetchData()
      this.saveDzenFiltersToCache()
    }, 500),

    onPageChange(page) {
      this.setDzenPagination({ currentPage: page })
      this.fetchData()
    },

    updateSort(sortBy) {
      if (sortBy && sortBy.length > 0) {
        this.setDzenSorting({ sort_by: sortBy[0].key, sort_order: sortBy[0].order })
      } else {
        this.setDzenSorting({ sort_by: '', sort_order: 'desc' })
      }
      // a new order starts from the first page
      this.setDzenPagination({ currentPage: 1 })
      this.fetchData()
    },

    toggleSelectAll(val) {
      if (val) {
        this.selectedItems = this.sources.map(s => s.id)
      } else {
        this.selectedItems = []
      }
    },

    async fetchData() {
      try {
        this.loading = true
        const f = this.dzenFilters
        const params = {
          offset: (f.currentPage - 1) * f.limit,
          limit: f.limit,
        }
        if (f.sort_by) params.sort_by = f.sort_by
        if (f.sort_order) params.sort_order = f.sort_order
        if (f.search) params.search = f.search
        if (f.description) params.description = f.description
        if (f.status) params.status = f.status
        if (f.topic_id) params.topic_id = f.topic_id
        if (f.subscribers_from) params.subscribers_from = f.subscribers_from
        if (f.subscribers_to) params.subscribers_to = f.subscribers_to
        if (f.messages_from) params.messages_from = f.messages_from
        if (f.messages_to) params.messages_to = f.messages_to
        if (f.updated_from) params.updated_from = f.updated_from
        if (f.updated_to) params.updated_to = f.updated_to
        if (f.is_monitoring !== null && f.is_monitoring !== undefined) {
          params.is_monitoring = f.is_monitoring
        }
        if (f.geolocation_id) params.geolocation_id = f.geolocation_id
        if (f.tags && f.tags.length > 0) {
          params.tags = f.tags.map(tag => tag.id || tag).join(',')
        }

        const response = await apiClient.get('/v2/dzen-sources', { params })
        this.sources = response.data.items || []
        const total = response.data.total || 0
        this.setDzenPagination({ totalItems: total, totalPages: Math.ceil(total / f.limit) || 1 })

        // Start polling if any sources are being processed
        if (this.processingSourceIds.length > 0) {
          this.startProcessingPoll()
        }
      } catch (error) {
        console.error('Error fetching Dzen sources:', error)
        this.showSnackbar('Ошибка загрузки источников', 'error')
      } finally {
        this.loading = false
      }
    },

    async toggleMonitoring(item) {
      try {
        const action = item.is_monitoring ? 'stop' : 'start'
        await apiClient.post(`/v2/dzen-sources/${item.id}/${action}`)
        item.is_monitoring = !item.is_monitoring
        this.showSnackbar(`Мониторинг ${item.is_monitoring ? 'включен' : 'выключен'}: ${item.display_name}`)
      } catch (error) {
        console.error('Error toggling monitoring:', error)
        this.showSnackbar('Ошибка изменения мониторинга', 'error')
      }
    },

    async executeBulkAction(action) {
      if (!action || this.selectedItems.length === 0) return

      if (action === 'edit') {
        this.openBulkEditModal()
        return
      }

      try {
        const source_ids = this.selectedItems
        await apiClient.post(`/v2/dzen-sources/bulk/${action}`, { source_ids })
        this.showSnackbar(`Операция выполнена для ${source_ids.length} источников`)
        this.selectedItems = []
        this.selectedBulkAction = ''
        if (action === 'update-info') {
          this.sources.forEach(s => {
            if (source_ids.includes(s.id)) s.is_processing = true
          })
          this.startProcessingPoll()
        } else {
          await this.fetchData()
        }
      } catch (error) {
        console.error('Error executing bulk action:', error)
        this.showSnackbar('Ошибка массовой операции', 'error')
      }
    },

    // Messages modal
    async openMessagesModal(source) {
      this.messagesModal.source = source
      this.messagesModal.visible = true
      this.messagesModal.items = []
      this.messagesModal.offset = 0
      this.messagesModal.hasMore = true
      this.messagesModal.totalMessages = 0
      this.messagesModal.loading = true
      await this.loadMessages()
    },

    async loadMessages() {
      if (this.messagesModal.loading === false && !this.messagesModal.hasMore) return
      this.messagesModal.loading = true

      try {
        const response = await apiDataClient.get('/source-content/by-source', {
          params: {
            source_id: this.messagesModal.source.id,
            offset: this.messagesModal.offset,
            limit: 20
          }
        })
        const newItems = (response.data.items || []).map(item => ({
          ...item,
          _expanded: false
        }))

        if (this.messagesModal.offset === 0) {
          this.messagesModal.items = newItems
          this.messagesModal.totalMessages = response.data.total || 0
        } else {
          this.messagesModal.items.push(...newItems)
        }

        this.messagesModal.offset += 20
        this.messagesModal.hasMore = newItems.length === 20 && this.messagesModal.items.length < (response.data.total || 0)
      } catch (error) {
        console.error('Error loading messages:', error)
        this.messagesModal.hasMore = false
      } finally {
        this.messagesModal.loading = false
      }
    },

    onMessagesScroll() {
      const container = this.$refs.messagesContainer
      if (!container) return
      const threshold = 100
      const position = container.scrollTop + container.clientHeight
      const height = container.scrollHeight
      if (position >= height - threshold && this.messagesModal.hasMore && !this.messagesModal.loading) {
        this.loadMessages()
      }
    },

    // Update source info (parse channel page)
    async updateSourceInfo(item) {
      try {
        await apiClient.post(`/v2/dzen-sources/${item.id}/update-info`)
        item.is_processing = true
        this.showSnackbar(`Обновление информации запрошено: ${item.display_name}`)
        this.startProcessingPoll()
      } catch (error) {
        const msg = error.response?.data?.detail || 'Ошибка запроса обновления'
        this.showSnackbar(msg, 'error')
      }
    },

    // Polling for is_processing status
    startProcessingPoll() {
      if (this.processingPollInterval) return
      this.processingPollInterval = setInterval(() => {
        this.checkProcessingStatus()
      }, 5000)
    },

    stopProcessingPoll() {
      if (this.processingPollInterval) {
        clearInterval(this.processingPollInterval)
        this.processingPollInterval = null
      }
    },

    async checkProcessingStatus() {
      const processingIds = this.processingSourceIds
      if (processingIds.length === 0) {
        this.stopProcessingPoll()
        return
      }

      try {
        for (const sourceId of processingIds) {
          const response = await apiClient.get(`/v2/dzen-sources/${sourceId}`)
          const updated = response.data
          if (!updated.is_processing) {
            // Update the row in-place
            const idx = this.sources.findIndex(s => s.id === sourceId)
            if (idx !== -1) {
              Object.assign(this.sources[idx], updated)
            }
            this.showSnackbar(`Информация обновлена: ${updated.display_name}`)
          }
        }
        // Stop polling if no more processing
        if (this.processingSourceIds.length === 0) {
          this.stopProcessingPoll()
        }
      } catch (error) {
        console.error('Error checking processing status:', error)
      }
    },

    // Edit modal
    openEditModal(item) {
      this.editModal.source = item
      this.editModal.form.section = item.topic_ids && item.topic_ids.length > 0 ? item.topic_ids[0] : null
      this.editModal.form.country = item.geolocation_id
      this.editModal.form.status = item.status
      this.editModal.form.tags = (item.tags || []).map(t => ({ id: t.id, name: t.name }))
      this.editTagSuggestions = []
      this.editCurrentSearchValue = ''
      // Pre-fill geo suggestions with current value
      if (item.geolocation_id && item.geolocation_name) {
        this.geoSuggestions = [{ label: item.geolocation_name, value: item.geolocation_id }]
      } else {
        this.geoSuggestions = []
      }
      this.editModal.visible = true
    },

    async saveEdit() {
      this.editModal.saving = true
      try {
        const tagsToSend = this._prepareTags(this.editModal.form.tags)
        const payload = {
          topic_id: this.editModal.form.section,
          geolocation_id: this.editModal.form.country,
          status: this.editModal.form.status,
          tags: tagsToSend,
        }
        await apiClient.put(`/v2/dzen-sources/${this.editModal.source.id}`, payload)
        this.editModal.visible = false
        this.showSnackbar('Источник обновлён')
        await this.fetchData()
      } catch (error) {
        console.error('Error saving edit:', error)
        this.showSnackbar('Ошибка сохранения', 'error')
      } finally {
        this.editModal.saving = false
      }
    },

    async searchEditTags(query) {
      this.editCurrentSearchValue = query || ''
      if (!query) {
        this.editTagSuggestions = []
        return
      }
      try {
        const resp = await apiClient.get('/tags', { params: { q: query } })
        this.editTagSuggestions = resp.data.map(t => ({ id: t.id, name: t.name }))
      } catch (e) {
        this.editTagSuggestions = []
      }
    },

    handleEditEnterKey(event) {
      if (this.editCurrentSearchValue && this.editCurrentSearchValue.trim()) {
        event.preventDefault()
        this._addNewTag(this.editModal.form.tags, this.editCurrentSearchValue.trim(), 'editTagAutocomplete')
        this.editCurrentSearchValue = ''
      }
    },

    onEditTagSelected() {
      this.editCurrentSearchValue = ''
      this.$nextTick(() => {
        if (this.$refs.editTagAutocomplete) {
          try {
            this.$refs.editTagAutocomplete.search = ''
            this.$refs.editTagAutocomplete.focus()
          } catch (e) { /* ignore */ }
        }
      })
    },

    // Bulk edit methods
    openBulkEditModal() {
      const selected = this.sources.filter(s => this.selectedItems.includes(s.id))

      // Pre-load common values: if all selected share the same value, show it
      const commonVal = (getter) => {
        const vals = selected.map(getter).filter(v => v != null)
        if (vals.length === selected.length && new Set(vals).size === 1) return vals[0]
        return null
      }

      this.bulkEditModal.form.country = commonVal(s => s.geolocation_id)
      this.bulkEditModal.form.section = commonVal(s => s.topic_ids && s.topic_ids.length > 0 ? s.topic_ids[0] : null)
      this.bulkEditModal.form.status = commonVal(s => s.status)

      // Tags: intersection of all selected sources' tags
      if (selected.length > 0 && selected.every(s => s.tags && s.tags.length > 0)) {
        const first = selected[0].tags.map(t => ({ id: t.id, name: t.name }))
        this.bulkEditModal.form.tags = first.filter(tag =>
          selected.every(s => s.tags.some(t => t.id === tag.id))
        )
      } else {
        this.bulkEditModal.form.tags = []
      }

      // Save initial state to detect what user changed
      this.bulkEditModal.initialForm = JSON.parse(JSON.stringify(this.bulkEditModal.form))

      this.bulkEditTagSuggestions = []
      this.bulkEditCurrentSearchValue = ''
      this.bulkEditModal.visible = true
    },

    closeBulkEditModal() {
      this.bulkEditModal.visible = false
      this.bulkEditModal.form.tags = []
      this.bulkEditModal.form.country = null
      this.bulkEditModal.form.section = null
      this.bulkEditModal.form.status = null
      this.bulkEditTagSuggestions = []
      this.bulkEditCurrentSearchValue = ''
      this.selectedBulkAction = ''
    },

    async searchBulkEditTags(query) {
      this.bulkEditCurrentSearchValue = query || ''
      if (!query) {
        this.bulkEditTagSuggestions = []
        return
      }
      try {
        const resp = await apiClient.get('/tags', { params: { q: query } })
        this.bulkEditTagSuggestions = resp.data.map(t => ({ id: t.id, name: t.name }))
      } catch (e) {
        this.bulkEditTagSuggestions = []
      }
    },

    handleBulkEditEnterKey(event) {
      if (this.bulkEditCurrentSearchValue && this.bulkEditCurrentSearchValue.trim()) {
        event.preventDefault()
        this._addNewTag(this.bulkEditModal.form.tags, this.bulkEditCurrentSearchValue.trim(), 'bulkEditTagAutocomplete')
        this.bulkEditCurrentSearchValue = ''
      }
    },

    onBulkEditTagSelected() {
      this.bulkEditCurrentSearchValue = ''
      this.$nextTick(() => {
        if (this.$refs.bulkEditTagAutocomplete) {
          try {
            this.$refs.bulkEditTagAutocomplete.search = ''
            this.$refs.bulkEditTagAutocomplete.focus()
          } catch (e) { /* ignore */ }
        }
      })
    },

    async saveBulkEdit() {
      try {
        const init = this.bulkEditModal.initialForm || {}
        const form = this.bulkEditModal.form
        const payload = { source_ids: this.selectedItems }

        // Only send fields that were changed (including cleared → null)
        if (form.country !== init.country) payload.geolocation_id = form.country
        if (form.section !== init.section) payload.topic_id = form.section
        if (form.status !== init.status) payload.status = form.status

        const tagsChanged = JSON.stringify((form.tags || []).map(t => t.id || t.name)) !==
                            JSON.stringify((init.tags || []).map(t => t.id || t.name))
        if (tagsChanged) payload.tags = this._prepareTags(form.tags)

        await apiClient.post('/v2/dzen-sources/bulk/edit', payload)
        this.showSnackbar(`Обновлено ${this.selectedItems.length} источников`)
        this.closeBulkEditModal()
        this.selectedItems = []
        this.selectAll = false
        await this.fetchData()
      } catch (error) {
        console.error('Error bulk editing:', error)
        this.showSnackbar('Ошибка массового редактирования', 'error')
      }
    },

    hasBulkEditChanges() {
      const init = this.bulkEditModal.initialForm || {}
      const form = this.bulkEditModal.form
      return form.country !== init.country ||
             form.section !== init.section ||
             form.status !== init.status ||
             JSON.stringify((form.tags || []).map(t => t.id || t.name)) !==
             JSON.stringify((init.tags || []).map(t => t.id || t.name))
    },

    // Tag helpers
    _prepareTags(tags) {
      return tags.map(tag => {
        if (typeof tag === 'object' && tag !== null && tag.id) return { id: tag.id }
        if (typeof tag === 'object' && tag !== null && tag.name && !tag.id) return { name: tag.name }
        if (typeof tag === 'string') return { name: tag }
        return null
      }).filter(Boolean)
    },

    _addNewTag(tagArray, tagName, refName) {
      if (!tagName) return
      const exists = tagArray.find(t =>
        (t.name && t.name.toLowerCase() === tagName.toLowerCase()) ||
        (typeof t === 'string' && t.toLowerCase() === tagName.toLowerCase())
      )
      if (exists) return
      tagArray.push({ name: tagName })
      this.$nextTick(() => {
        if (this.$refs[refName]) {
          try { this.$refs[refName].search = '' } catch (e) { /* ignore */ }
        }
      })
    },

    // Delete
    confirmDelete(item) {
      this.deleteDialog.source = item
      this.deleteDialog.visible = true
    },

    async deleteSource() {
      this.deleteDialog.deleting = true
      try {
        await apiClient.delete(`/v2/dzen-sources/${this.deleteDialog.source.id}`)
        this.deleteDialog.visible = false
        this.showSnackbar('Источник удалён')
        await this.fetchData()
      } catch (error) {
        console.error('Error deleting source:', error)
        this.showSnackbar('Ошибка удаления', 'error')
      } finally {
        this.deleteDialog.deleting = false
      }
    },

    resetFilters() {
      this.resetDzenFilters()
      this.availableTags = []
      this.filterGeoSuggestions = []
      this.fetchData()
    },

    async loadReferenceData() {
      try {
        const [resTopics, resCountries] = await Promise.all([
          apiClient.get('/reference/topics'),
          apiClient.get('/reference/geolocations/countries')
        ])
        this.sections = resTopics.data.items.map(item => ({
          option: item.name,
          value: item.id
        }))
        this.countries = resCountries.data.map(item => ({
          option: item.name,
          value: item.id
        }))
      } catch (error) {
        console.error('Error loading reference data:', error)
      }
    },

    async searchGeo(query) {
      if (!query || query.length < 2) return
      try {
        const resp = await apiClient.get('/reference/geolocations', { params: { name: query, limit: 20 } })
        const levelLabels = { country: 'страна', district: 'округ', region: 'регион', city: 'город' }
        this.geoSuggestions = (resp.data.items || []).map(item => ({
          label: `${item.name} (${levelLabels[item.level] || item.level})`,
          value: item.id
        }))
      } catch (e) {
        console.error('Error searching geolocations:', e)
      }
    },

    async searchFilterGeo(query) {
      if (!query || query.length < 2) return
      try {
        const resp = await apiClient.get('/reference/geolocations', { params: { name: query, limit: 20 } })
        const levelLabels = { country: 'страна', district: 'округ', region: 'регион', city: 'город' }
        this.filterGeoSuggestions = (resp.data.items || []).map(item => ({
          label: `${item.name} (${levelLabels[item.level] || item.level})`,
          value: item.id
        }))
      } catch (e) {
        console.error('Error searching geolocations:', e)
      }
    },

    async searchBulkGeo(query) {
      if (!query || query.length < 2) return
      try {
        const resp = await apiClient.get('/reference/geolocations', { params: { name: query, limit: 20 } })
        const levelLabels = { country: 'страна', district: 'округ', region: 'регион', city: 'город' }
        this.bulkGeoSuggestions = (resp.data.items || []).map(item => ({
          label: `${item.name} (${levelLabels[item.level] || item.level})`,
          value: item.id
        }))
      } catch (e) {
        console.error('Error searching geolocations:', e)
      }
    },

    async searchAvailableTags(query) {
      if (!query) {
        this.availableTags = []
        return
      }
      try {
        const resp = await apiClient.get('/tags', { params: { q: query } })
        this.availableTags = resp.data.map(t => ({ id: t.id, name: t.name }))
      } catch (e) {
        this.availableTags = []
      }
    },

    onTagsUpdate(tags) {
      this.updateDzenFilter({ field: 'tags', value: tags })
      this.$nextTick(() => {
        if (this.$refs.tagFilterAutocomplete) {
          try {
            this.$refs.tagFilterAutocomplete.search = ''
            this.$refs.tagFilterAutocomplete.focus()
          } catch (e) {
            // ignore ref access errors
          }
        }
      })
      this.setDzenPagination({ currentPage: 1 })
      this.fetchData()
    },

    // Helpers
    formatISODate(isoString) {
      if (!isoString) return 'не обновлялся'
      const date = new Date(isoString)
      if (isNaN(date.getTime()) || date.getFullYear() === 1970) return 'не обновлялся'
      const dd = String(date.getDate()).padStart(2, '0')
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const yyyy = date.getFullYear()
      const HH = String(date.getHours()).padStart(2, '0')
      const MM = String(date.getMinutes()).padStart(2, '0')
      const ss = String(date.getSeconds()).padStart(2, '0')
      return `${dd}.${mm}.${yyyy} ${HH}:${MM}:${ss}`
    },

    formatDateOnly(isoString) {
      if (!isoString) return ''
      const date = new Date(isoString)
      if (isNaN(date.getTime())) return ''
      const dd = String(date.getDate()).padStart(2, '0')
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const yyyy = date.getFullYear()
      return `${dd}.${mm}.${yyyy}`
    },

    formatNumber(num) {
      if (num === null || num === undefined) return '—'
      return num.toLocaleString('ru-RU')
    },

    formatFrequency(seconds) {
      if (!seconds) return '—'
      if (seconds < 3600) return `${Math.round(seconds / 60)} мин.`
      return `${Math.round(seconds / 3600)} ч.`
    },

    statusLabel(status) {
      const labels = {
        active: 'Активен',
        pending: 'В ожидании',
        blocked: 'Заблокирован',
        deleted: 'Удалён',
        error: 'Ошибка'
      }
      return labels[status] || status || 'В ожидании'
    },

    statusColor(status) {
      const colors = {
        active: 'success',
        pending: 'warning',
        blocked: 'error',
        deleted: 'grey',
        error: 'error'
      }
      return colors[status] || 'default'
    },

    topicNames(topicIds) {
      return (topicIds || [])
        .map(id => {
          const topic = this.sections.find(s => s.value === id)
          return topic ? topic.option : null
        })
        .filter(Boolean)
    },


    truncateDescription(desc) {
      if (!desc) return '—'
      return desc.length > 60 ? desc.substring(0, 60) + '...' : desc
    },

    truncateText(text, maxLen) {
      if (!text) return ''
      return text.length > maxLen ? text.substring(0, maxLen) + '...' : text
    },

    dzenPublisher(msg) {
      // Dzen exposes the channel under platform_data.publisher_url —
      // source_url itself is the article URL (https://dzen.ru/a/{id}),
      // which is NOT a channel handle. Use publisher_name as the label
      // and derive the handle from publisher_url for the @ prefix.
      const pd = msg?.platform_data || {}
      const url = pd.publisher_url || ''
      let handle = ''
      if (url) {
        const m = url.match(/^https?:\/\/(?:www\.)?dzen\.ru\/([^/?#]+)(?:\/|$)/i)
        if (m && m[1] !== 'a') handle = m[1]
      }
      const label = pd.publisher_name || (handle ? `@${handle}` : '')
      return { url: url && handle ? url : '', label, handle }
    },

    showSnackbar(text, color = 'success') {
      this.snackbar.text = text
      this.snackbar.color = color
      this.snackbar.visible = true
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.table-column-transition {
  transition: flex-basis 0.3s ease, max-width 0.3s ease;
}

.text-right {
  text-align: right !important;
}

/* Messages modal styles (matches Telegram) */
.messages-container {
  height: 70vh;
  overflow-y: auto;
  padding: 1rem;
}

.message-item {
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 0;
}

.message-item:last-child {
  border-bottom: none;
}

.message-date {
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.9rem;
}

.message-link {
  margin-top: 0.25rem;
}

.message-link a {
  text-decoration: none;
  font-size: 0.85rem;
}

.message-link a:hover {
  text-decoration: underline;
}

.message-content {
  line-height: 1.5;
}

.article-title {
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: rgb(var(--v-theme-on-surface));
}

.main-text {
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 0.5rem;
}

.messages-container::-webkit-scrollbar {
  width: 8px;
}

.messages-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.messages-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.author-header {
  display: flex;
  align-items: center;
}
.author-name {
  font-weight: 600;
  line-height: 1.1;
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.95rem;
}
.author-handle {
  font-size: 0.8rem;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}
.author-handle:hover {
  text-decoration: underline;
}
</style>

<!-- Unscoped styles for Vuetify deep overrides -->
<style>
.dzen-sources-page .v-data-table .v-data-table__th {
  padding: 8px 16px !important;
  font-weight: 600 !important;
  white-space: nowrap;
}

.dzen-sources-page .v-data-table .v-data-table__td {
  padding: 8px 16px !important;
  height: 60px !important;
}

.dzen-sources-page .v-data-table .v-data-table__td:not(:nth-child(1)):not(:nth-child(2)),
.dzen-sources-page .v-data-table .v-data-table__th:not(:nth-child(1)):not(:nth-child(2)) {
  padding-left: 8px !important;
  padding-right: 8px !important;
}

.dzen-sources-page .v-data-table .v-data-table__td:nth-child(2) {
  padding-left: 20px !important;
}

.dzen-sources-page .v-btn--size-x-small {
  height: 28px !important;
  width: 28px !important;
}
</style>
